<template>
  <v-card>
    <v-card-title class="d-flex align-center flex-wrap">
      <span class="text-h5">{{ $t('global.adminBilling.deviceSubscriptions') }}</span>
      <v-spacer />
      <div class="d-flex align-center flex-wrap filters-wrap">
        <!-- Company Filter -->
        <v-select
          v-model="filters.companyId"
          :items="companiesOptions"
          item-text="name"
          item-value="id"
          label="Firma"
          hide-details
          dense
          outlined
          clearable
          style="min-width: 200px"
        />

        <!-- SIM Status Filter -->
        <v-select
          v-model="filters.deviceType"
          :items="deviceTypesOptions"
          label="Type"
          hide-details
          dense
          outlined
          clearable
          style="min-width: 180px"
        />

        <!-- Billing Filter -->
        <v-select
          v-model="filters.billingEnabled"
          :items="billingEnabledOptions"
          :label="$t('global.adminBilling.filterByBilling')"
          hide-details
          dense
          outlined
          clearable
          style="min-width: 180px"
        />

        <!-- Activation State Filter -->
        <v-select
          v-model="filters.activationState"
          :items="activationStateOptions"
          label="Stav aktivace"
          hide-details
          dense
          outlined
          style="min-width: 180px"
        />

        <!-- SIM Status Filter -->
        <v-select
          v-model="filters.simStatus"
          :items="simStatusOptions"
          label="SIM Status"
          hide-details
          dense
          outlined
          clearable
          style="min-width: 180px"
        />

        <!-- Mobile Connection Filter -->
        <v-select
          v-model="filters.mobileConnection"
          :items="booleanFilterOptions"
          label="Mobile Connection"
          hide-details
          dense
          outlined
          clearable
          style="min-width: 180px"
        />

        <!-- In Operation Filter -->
        <v-select
          v-model="filters.inOperation"
          :items="booleanFilterOptions"
          label="In Operation"
          hide-details
          dense
          outlined
          clearable
          style="min-width: 180px"
        />

        <!-- Event Count Filter -->
        <v-text-field
          v-model.number="filters.mobileNotConfEventCountGt"
          label="Počet eventů větší než"
          type="number"
          min="0"
          hide-details
          dense
          outlined
          clearable
          style="min-width: 100px"
        />

        <!-- Event Count Filter (Less Than) -->
        <v-text-field
          v-model.number="filters.mobileNotConfEventCountLt"
          label="Počet eventů menší než"
          type="number"
          min="0"
          hide-details
          dense
          outlined
          clearable
          style="min-width: 100px"
        />

        <!-- Search Input -->
        <v-text-field
          v-model="filters.search"
          :label="$t('global.adminBilling.searchDevices')"
          prepend-inner-icon="mdi-magnify"
          hide-details
          dense
          outlined
          style="min-width: 300px"
        />

        <!-- Status Filter -->
        <v-select
          v-model="filters.status"
          :items="statusOptions"
          :label="$t('global.adminBilling.filterByStatus')"
          hide-details
          dense
          outlined
          clearable
          style="min-width: 180px"
        />
      </div>
    </v-card-title>

    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="devices"
        :loading="$apollo.loading"
        :server-items-length="totalCount"
        :page.sync="currentPage"
        :items-per-page.sync="itemsPerPage"
        :footer-props="{
          'items-per-page-options': [10, 20, 50, 100],
          'items-per-page-text': $t('global.dataTable.itemsPerPageText'),
        }"
      >
        <!-- Device Name Column -->
        <template v-slot:item.customName="{ item }">
          <div>
            <a :href="deviceDetailUrl(item.id)" target="_blank" rel="noopener noreferrer">
              <strong>{{ item.customName || 'Unnamed Device' }}</strong>
            </a>
            <div class="text-caption grey--text">{{ item.controller }}</div>
          </div>
        </template>

        <!-- Company Column -->
        <template v-slot:item.companyName="{ item }">
          {{ item.companyName }}
        </template>

        <!-- Status Column -->
        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" text-color="white" small>
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <!-- Days Remaining Column -->
        <template v-slot:item.daysRemaining="{ item }">
          <span v-if="item.daysRemaining !== null"> {{ item.daysRemaining }} days </span>
          <span v-else class="grey--text">N/A</span>
        </template>

        <!-- Valid From Column -->
        <template v-slot:item.validFrom="{ item }">
          <span v-if="item.validFrom">
            {{ formatDate(item.validFrom) }}
          </span>
          <span v-else class="grey--text">N/A</span>
        </template>

        <!-- inOperationSince Column -->
        <template v-slot:item.inOperationSince="{ item }">
          <span v-if="item.inOperationSince">
            {{ formatDate(item.inOperationSince) }}
          </span>
          <span v-else class="grey--text">N/A</span>
        </template>

        <!-- Valid To Column -->
        <template v-slot:item.validTo="{ item }">
          <span v-if="item.validTo">
            {{ formatDate(item.validTo) }}
          </span>
          <span v-else class="grey--text">N/A</span>
        </template>

        <template v-slot:item.connectionType="{ item }">
          {{ item.connectionType }}
        </template>

        <!-- User Count Column -->
        <template v-slot:item.userCount="{ item }">
          {{ item.userCount }}
        </template>

        <!-- SIM Status Column -->
        <template v-slot:item.simStatus="{ item }">
          <div class="d-flex align-center gap-2">
            <span v-if="item.simStatus">{{ item.simStatus }}</span>
            <span v-else class="grey--text">N/A</span>
            <v-btn
              icon
              x-small
              :loading="refreshingSimStatus === item.id"
              @click="refreshSimStatus(item)"
            >
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- Billing Column -->
        <template v-slot:item.billing="{ item }">
          <v-btn
            small
            :color="item.activationState ? 'success' : 'error'"
            @click="$emit('set-activation-state', item)"
          >
            {{
              item.activationState ? item.activationState?.replace('_', ' ') : 'Billing disabled'
            }}
          </v-btn>
        </template>

        <!-- Actions Column -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex gap-2">
            <v-btn small color="primary" @click="$emit('set-status', item)"> Set Status </v-btn>

            <v-btn small color="success" @click="$emit('gift-subscription', item)">
              Gift Months
            </v-btn>

            <v-btn
              small
              color="orange"
              @click="$emit('send-billing-reminder', item)"
              :disabled="!item.deviceBillingEnabled"
            >
              Send Email
            </v-btn>

            <v-btn small color="info" @click="$emit('show-debug', item)"> Show Debug </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { adminBillingDevices } from '@/graphql/query/adminBillingDevices'
import { companiesOptions } from '@/graphql/query/companies'
import { adminRefreshSimStatus } from '@/graphql/mutations/adminRefreshSimStatus'

export default {
  name: 'BillingDeviceTable',

  data() {
    return {
      devices: [],
      totalCount: 0,
      currentPage: 1,
      itemsPerPage: 20,
      refreshingSimStatus: null,
      filters: {
        search: '',
        status: null,
        billingEnabled: true,
        activationState: 'ALL',
        companyId: null,
        simStatus: null,
        deviceType: null,
        mobileConnection: null,
        inOperation: null,
        mobileNotConfEventCountGt: null,
        mobileNotConfEventCountLt: null,
      },

      statusOptions: [
        { text: this.$t('global.adminBilling.billingOptions.all'), value: null },
        { text: 'Valid', value: 'VALID' },
        { text: 'Soon Expiring', value: 'SOON_EXPIRING' },
        { text: 'Expired', value: 'EXPIRED' },
        { text: 'Unpaid', value: 'UNPAID' },
        { text: 'Blocked', value: 'BLOCKED' },
        { text: 'Pending for reactivation', value: 'REACTIVATION_PENDING' },
        { text: 'Not Defined', value: 'NOT_DEFINED' },
      ],

      simStatusOptions: [
        { text: this.$t('global.adminBilling.billingOptions.all'), value: null },
        { text: 'No SIM', value: 'noSim' },
        { text: 'Active', value: 'active' },
        { text: 'Barred', value: 'barred' },
        { text: 'Inactive', value: 'inactive' },
        { text: 'Not found in MSS', value: 'notFoundInMSS' },
        { text: 'Unknown', value: 'unknown' },
        { text: 'Cancelled', value: 'cancelled' },
        { text: 'Cancellation pending', value: 'cancellationPending' },
      ],

      deviceTypesOptions: [
        { text: this.$t('global.adminBilling.billingOptions.all'), value: null },
        { text: 'Charge point', value: 'CHARGE_POINT' },
        { text: 'Pool', value: 'POOL' },
        { text: 'Retention tank', value: 'RTANK' },
        { text: 'Sewage pump', value: 'SEWAGE_PUMP' },
      ],

      activationStateOptions: [
        { text: this.$t('global.adminBilling.billingOptions.all'), value: 'ALL' },
        { text: 'Introductory period', value: 'INTRODUCTORY_PERIOD' },
        { text: 'Accepted', value: 'ACCEPTED' },
        { text: 'Active', value: 'ACTIVE' },
        { text: 'Declined', value: 'DECLINED' },
        { text: 'Free', value: 'FREE' },
        { text: 'Vypnuto', value: null },
      ],

      headers: [
        { text: 'Device', value: 'customName', sortable: false },
        { text: 'Company', value: 'companyName', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Days Remaining', value: 'daysRemaining', sortable: false },
        { text: 'Valid From', value: 'validFrom', sortable: false },
        { text: 'Valid To', value: 'validTo', sortable: false },
        { text: 'In operation', value: 'inOperationSince', sortable: false },
        { text: 'Users', value: 'userCount', sortable: false },
        { text: 'SIM Status', value: 'simStatus', sortable: false },
        { text: 'Connection type', value: 'connectionType', sortable: false },
        { text: 'Billing', value: 'billing', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, width: '350px' },
      ],
    }
  },

  computed: {
    billingEnabledOptions() {
      return [
        { text: this.$t('global.adminBilling.billingOptions.all'), value: null },
        { text: this.$t('global.adminBilling.billingOptions.enabled'), value: true },
        { text: this.$t('global.adminBilling.billingOptions.disabled'), value: false },
      ]
    },
    booleanFilterOptions() {
      return [
        { text: this.$t('global.adminBilling.billingOptions.all'), value: null },
        { text: this.$t('global.yes'), value: true },
        { text: this.$t('global.no'), value: false },
      ]
    },
  },

  apollo: {
    adminBillingDevices: {
      query: adminBillingDevices,
      // Debounces the automatic refetch vue-apollo already triggers whenever a reactive
      // dependency read inside variables() (any filter, currentPage, itemsPerPage) changes -
      // do not also call refetch() manually on filter/pagination events, that fires it twice.
      debounce: 500,
      variables() {
        return {
          search: this.filters.search || undefined,
          status: this.filters.status || undefined,
          billingEnabled:
            this.filters.billingEnabled !== null ? this.filters.billingEnabled : undefined,
          activationState:
            this.filters.activationState === 'ALL' ? undefined : this.filters.activationState,
          companyId: this.filters.companyId || undefined,
          simStatus: this.filters.simStatus || undefined,
          deviceType: this.filters.deviceType || undefined,
          mobileConnection:
            this.filters.mobileConnection !== null ? this.filters.mobileConnection : undefined,
          inOperation: this.filters.inOperation !== null ? this.filters.inOperation : undefined,
          mobileNotConfEventCountGt: Number.isFinite(this.filters.mobileNotConfEventCountGt)
            ? this.filters.mobileNotConfEventCountGt
            : undefined,
          mobileNotConfEventCountLt: Number.isFinite(this.filters.mobileNotConfEventCountLt)
            ? this.filters.mobileNotConfEventCountLt
            : undefined,
          page: this.currentPage,
          limit: this.itemsPerPage,
        }
      },
      update(data) {
        this.devices = data.adminBillingDevices.devices
        this.totalCount = data.adminBillingDevices.totalCount
        return data.adminBillingDevices
      },
      error(error) {
        this.$toast.error('Failed to load billing devices: ' + error.message)
      },
    },
    companiesOptions: {
      query: companiesOptions,
      update(data) {
        return data.companies.edges
          .map((companyEdge) => companyEdge.node)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      },
    },
  },

  methods: {
    refetchData() {
      this.$apollo.queries.adminBillingDevices.refetch()
    },

    // Public method for parent to refresh data
    refresh() {
      this.refetchData()
    },

    getStatusColor(status) {
      switch (status) {
        case 'VALID':
          return 'success'
        case 'SOON_EXPIRING':
          return 'warning'
        case 'EXPIRED':
          return 'error'
        case 'UNPAID':
          return 'error'
        case 'BLOCKED':
          return 'error'
        case 'REACTIVATION_PENDING':
          return 'warning'
        case 'NOT_DEFINED':
          return 'grey'
        default:
          return 'grey'
      }
    },

    getStatusText(status) {
      switch (status) {
        case 'VALID':
          return 'Valid'
        case 'SOON_EXPIRING':
          return 'Soon Expiring'
        case 'EXPIRED':
          return 'Expired'
        case 'NOT_DEFINED':
          return 'Not Defined'
        case 'UNPAID':
          return 'Unpaid'
        case 'BLOCKED':
          return 'Blocked'
        case 'REACTIVATION_PENDING':
          return 'Pending for reactivation'
        default:
          return status
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString()
    },

    deviceDetailUrl(deviceId) {
      return this.$router.resolve({ name: 'DevicePage', params: { deviceId } }).href
    },

    async refreshSimStatus(device) {
      this.refreshingSimStatus = device.id

      try {
        const { data } = await this.$apollo.mutate({
          mutation: adminRefreshSimStatus,
          variables: {
            input: {
              deviceId: device.id,
            },
          },
        })

        if (data.adminRefreshSimStatus.success) {
          // Apollo freezes query results (both the array and its items), so build a new array
          this.devices = this.devices.map((d) =>
            d.id === device.id ? { ...d, simStatus: data.adminRefreshSimStatus.simStatus } : d,
          )
          this.$toast.success('SIM status refreshed successfully')
        } else {
          this.$toast.error('Failed to refresh SIM status')
        }
      } catch (error) {
        console.error('Error refreshing SIM status:', error)
        this.$toast.error('Error refreshing SIM status: ' + error.message)
      } finally {
        this.refreshingSimStatus = null
      }
    },
  },
}
</script>

<style scoped>
.gap-2 > * + * {
  margin-left: 8px;
}

.filters-wrap {
  margin-top: 12px;
  column-gap: 16px;
  row-gap: 12px;
}

.filters-wrap > * {
  /* Prevent a lone wrapped item from being flex-grown to fill the whole row */
  flex: 0 0 auto;
}

@media (max-width: 600px) {
  .filters-wrap {
    width: 100%;
  }

  .filters-wrap > * {
    flex-basis: 100%;
    width: 100%;
  }
}
</style>
